import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

/**
 * Hỏi xác nhận **ngay cạnh nút vừa bấm** — thay cho `window.confirm`.
 *
 * Người dùng yêu cầu 2026-09-29: hộp `confirm` của trình duyệt mọc ở giữa trên
 * cùng màn hình, nên bấm Xoá xong phải kéo chuột lên tận đỉnh mới bấm được OK
 * — hai lần bấm cho một việc mà cách nhau cả màn hình. Popup này đặt nút xác
 * nhận ở mép **dưới-phải** của nút vừa bấm (`align="end"`), tức lần bấm thứ hai
 * chỉ cách lần thứ nhất một chiều cao popup.
 *
 * ## Mệnh lệnh, không bọc nút
 *
 * `ask()` chứ không phải `<ConfirmPopover><Button/></ConfirmPopover>`: một nửa
 * số chỗ cần hỏi là **mục trong menu** (Xoá thư mục, Xoá tệp), và menu đóng —
 * gỡ mục đó khỏi DOM — ngay khi mục được chọn. Không có phần tử nào còn sống
 * để bọc. Nên neo là một **hình chữ nhật** chụp lúc bấm: phần tử còn gắn vào
 * trang thì đọc vị trí sống (theo cuộn), đã bị gỡ thì dùng ảnh chụp.
 *
 * Neo lấy từ lần `pointerdown` gần nhất (nút / mục menu chứa điểm bấm), hoặc
 * phần tử đang có tiêu điểm khi bấm bằng bàn phím — chỗ gọi không phải truyền
 * gì, đúng như `window.confirm` trước đây.
 *
 * ```tsx
 * const confirm = useConfirmPopover();
 * onClick={() => confirm.ask({ message: question, onConfirm: () => remove.mutate() })}
 * {confirm.popover}
 * ```
 */
export function useConfirmPopover(): {
  readonly ask: (request: ConfirmRequest) => void;
  readonly popover: ReactNode;
} {
  const [pending, setPending] = useState<(ConfirmRequest & { anchor: Anchor }) | null>(null);

  const ask = useCallback((request: ConfirmRequest): void => {
    setPending({ ...request, anchor: anchorNow() });
  }, []);

  const popover = pending ? (
    <ConfirmPopoverPanel
      // Mỗi câu hỏi một lần mount: tiêu điểm và vị trí dựng lại từ đầu.
      key={pending.anchor.id}
      request={pending}
      anchor={pending.anchor}
      onDone={() => setPending(null)}
    />
  ) : null;

  return { ask, popover };
}

export interface ConfirmRequest {
  readonly message: string;
  /** Nhãn nút xác nhận. Bỏ trống = "Xoá". */
  readonly confirmLabel?: string;
  /** Nút xác nhận màu đỏ — mặc định `true`, vì gần như mọi câu hỏi ở đây là xoá. */
  readonly destructive?: boolean;
  readonly onConfirm: () => void;
}

interface Anchor {
  readonly id: number;
  readonly getBoundingClientRect: () => DOMRect;
}

function ConfirmPopoverPanel({
  request,
  anchor,
  onDone,
}: {
  readonly request: ConfirmRequest;
  readonly anchor: Anchor;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const virtualRef = useRef<Anchor>(anchor);
  const destructive = request.destructive ?? true;

  return (
    <PopoverPrimitive.Root open onOpenChange={(open) => (open ? undefined : onDone())}>
      <PopoverPrimitive.Anchor virtualRef={virtualRef} />
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          role="alertdialog"
          /*
            `aria-modal`: `Dialog` chỉ đóng khi nó là lớp modal **trên cùng**
            lúc nhấn Esc — nhờ thế Esc ở đây chỉ đóng popup, không gỡ luôn hộp
            thoại đang mở bên dưới (xoá việc trong popup chi tiết việc).
          */
          aria-modal="true"
          aria-label={request.message}
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={8}
          /*
            Menu vừa đóng trả tiêu điểm về nút mở menu — việc ấy xảy ra **sau**
            khi popup đã mở, và mặc định Radix coi đó là "tiêu điểm ra ngoài" rồi
            đóng popup ngay. Chỉ đóng bằng bấm ra ngoài hoặc Esc.
          */
          onFocusOutside={(event) => event.preventDefault()}
          // Không có nút kích hoạt nào để trả tiêu điểm về (neo là hình chữ nhật).
          onCloseAutoFocus={(event) => event.preventDefault()}
          /*
            Portal vẫn nằm **trong** cây React của chỗ gọi, nên click ở đây nổi
            lên tới hàng / thẻ cha — một cú bấm Huỷ không được mở luôn cái hàng.
          */
          onClick={(event) => event.stopPropagation()}
          className="popover-panel glass-popup-sm z-[60] flex w-72 max-w-[calc(100vw-1rem)] flex-col gap-3 rounded-DEFAULT border border-border bg-card p-3 text-foreground shadow-lg outline-none"
        >
          <p className="whitespace-pre-line text-sm">{request.message}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onDone}>
              {t('common.cancel')}
            </Button>
            {/* `autoFocus`: Enter là xác nhận, Esc là Huỷ — không cần chuột. */}
            <Button
              type="button"
              size="sm"
              variant={destructive ? 'destructive' : 'default'}
              autoFocus
              onClick={() => {
                onDone();
                request.onConfirm();
              }}
            >
              {request.confirmLabel ?? t('common.delete')}
            </Button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// ── Neo: chỗ người dùng vừa bấm ─────────────────────────────────────────────

/** Nút hay mục menu chứa điểm bấm — cái hộp mà người dùng nhìn thấy mình vừa bấm. */
const CLICKABLE = 'button, a, [role="menuitem"], [role="button"], [role="option"]';

/**
 * Lần `pointerdown` gần nhất trên toàn trang. Một listener cho cả ứng dụng, cài
 * một lần: rẻ hơn nhiều so với bắt mọi chỗ gọi truyền `event` vào.
 */
let lastPointer: { target: Element | null; x: number; y: number; at: number } | null = null;
let listening = false;
let nextId = 1;

function listen(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest(CLICKABLE) : null;
      lastPointer = { target, x: event.clientX, y: event.clientY, at: Date.now() };
    },
    { capture: true },
  );
}
listen();

function anchorNow(): Anchor {
  // Bấm chuột trong vòng vài giây ⇒ neo theo chuột; cũ hơn ⇒ người dùng đang
  // dùng bàn phím, neo theo phần tử đang có tiêu điểm.
  const recent = lastPointer && Date.now() - lastPointer.at < 5000 ? lastPointer : null;
  const element =
    recent?.target ??
    (recent ? null : document.activeElement !== document.body ? document.activeElement : null);

  const point = recent ? pointRect(recent.x, recent.y) : null;
  const snapshot = element?.getBoundingClientRect() ?? point ?? centre();

  return {
    id: nextId++,
    getBoundingClientRect: () =>
      element?.isConnected ? element.getBoundingClientRect() : snapshot,
  };
}

function centre(): DOMRect {
  return pointRect(window.innerWidth / 2, window.innerHeight / 3);
}

/** Hình chữ nhật 0×0 tại một điểm. Không `new DOMRect`: jsdom không có nó. */
function pointRect(x: number, y: number): DOMRect {
  const rect = { x, y, width: 0, height: 0, top: y, left: x, right: x, bottom: y };
  return { ...rect, toJSON: () => rect };
}
